import {StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";

import strings from "./strings.ts";
import {hasHtmlTags} from "./utils.ts";

const StyledTextarea = addStyle("textarea");

interface Props {
    text: string;
    setText: (value: string) => void;
    ariaLabel: string;
    maxLength?: number;
    disabled?: boolean;
    style?: CSSProperties;
    isDuplicate?: boolean;
}

const Field: React.FC<Props> = (props) => {
    const {
        text,
        setText,
        maxLength = 300,
        disabled = false,
        isDuplicate = false,
        ariaLabel,
    } = props;

    const validateText = (text: string): string | null => {
        if (!text.trim().length) {
            return strings.emptyFieldError();
        }
        if (hasHtmlTags(text)) {
            return strings.htmlTagError();
        }
        return null;
    };

    const errorMessage = isDuplicate
        ? strings.duplicateAnswerError()
        : validateText(props.text);
    const hasError = !!errorMessage;

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        setText(newValue);
    };
    const handleReturn = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter") {
            event.currentTarget.blur();
        }
    };

    /*
    Ideally, I'd like to make Field auto-resize. This is only possible
    by replicating the content into a resizable container, see:
    https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/
    */
    return (
        <View style={styles.container}>
            <View
                style={
                    hasError
                        ? [styles.errorBackground, styles.grow]
                        : [styles.grow]
                }
            >
                <StyledTextarea
                    style={[styles.textAreaBase, props.style]}
                    value={text}
                    onChange={handleChange}
                    onKeyDown={disabled ? undefined : handleReturn}
                    disabled={disabled}
                    maxLength={maxLength}
                    aria-label={ariaLabel}
                />
            </View>
            {hasError && (
                <LabelSmall
                    style={styles.errorText}
                    role="alert"
                    aria-live="polite"
                >
                    {errorMessage}
                </LabelSmall>
            )}
        </View>
    );
};

const SansFamily = 'Lato, "Noto Sans", sans-serif';
const styles = StyleSheet.create({
    container: {
        flexDirection: "column",
        width: "100%",
        display: "flex",
        flexGrow: 1,
    },
    grow: {
        display: "flex",
        flexGrow: 1,
    },
    textAreaBase: {
        display: "flex",
        flexGrow: 1,
        fontFamily: SansFamily,
        fontSize: 14,
        borderStyle: "none",
        margin: 0,
        padding: spacing.xxSmall_6,
        backgroundColor: "inherit",
        minBlockSize: 40,
        resize: "none",
    },
    errorBackground: {
        backgroundColor: semanticColor.core.background.critical.subtle,
    },
    errorText: {
        color: semanticColor.core.foreground.critical.subtle,
    },
});

export default Field;
